// Parts of a Studio element shared by its full sheet (web/ui/studio.js) and the « Brief » section
// of an ad's page (web/ui/studio-pub.js): its fields (long texts folded), the form of « Modifier »
// (PATCH {title?, data}), its files with « Ajouter des fichiers » (a role, each file sent as the
// request body) and their removal, and its history. Text written by agents is always shown as text.
import { h, button, iconButton, icon, select, input, textarea, field, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime, formatBytes } from '../../format.js';
import { statusInfo } from './model.js';
import './strings.js';

/** A long text is folded above this many characters. */
export const FOLD_CHARS = 600;
/** Largest file sent from the screens (server/modules/studio.js STUDIO_UPLOAD_BYTES). */
export const UPLOAD_MAX_BYTES = 60 * 1024 * 1024;
/** Files the « Ajouter des fichiers » picker offers. */
export const UPLOAD_ACCEPT = 'image/*,video/*,application/pdf';

const enc = encodeURIComponent;

/** The label of a file role (« Photo de base »…); an unknown role stays as written. */
export function roleLabel(role) {
  const key = `studio.role.${role || 'none'}`;
  const label = t(key);
  return label === key ? role : label;
}

/**
 * The fields of an element as rows of a <dl class="st-fields">: the type's fields in its order,
 * then the others; links, JSON, long texts folded above FOLD_CHARS. `empty`: the type's fields
 * without a value too (« — »), so the brief shows what is still to be written.
 * @param {{data: object}} item
 * @param {{fields?: Array<{key: string, label: string, type: string}>}|null} type
 * @param {{empty?: boolean}} [opts]
 * @returns {HTMLElement[]}
 */
export function fieldRows(item, type, { empty = false } = {}) {
  const defs = type?.fields || [];
  const data = item.data || {};
  const keys = [...defs.map((f) => f.key), ...Object.keys(data).filter((k) => !defs.some((f) => f.key === k))];
  const rows = [];
  for (const k of keys) {
    const has = Object.hasOwn(data, k) && !(empty && (data[k] === null || data[k] === ''));
    if (!has && !(empty && defs.some((f) => f.key === k))) continue;
    const def = defs.find((f) => f.key === k) || { key: k, label: k, type: 'text' };
    const v = data[k];
    let value;
    if (!has) {
      value = h('p', { class: 'st-value st-muted' }, '—');
    } else if (def.type === 'url' && typeof v === 'string') {
      value = h('a', { href: v, target: '_blank', rel: 'noopener noreferrer', class: 'st-link' }, v);
    } else if (def.type === 'json' || (v && typeof v === 'object')) {
      value = h('pre', { class: 'st-json' }, JSON.stringify(v, null, 2));
    } else {
      const text = String(v);
      if (text.length > FOLD_CHARS) {
        const full = h('p', { class: 'st-longtext' }, text);
        const short = h('p', { class: 'st-longtext is-short' }, `${text.slice(0, FOLD_CHARS).trimEnd()}…`);
        full.hidden = true;
        const toggle = button(t('studio.showMore', { n: text.length }), { variant: 'link', size: 'sm' });
        toggle.addEventListener('click', () => {
          full.hidden = !full.hidden;
          short.hidden = !full.hidden;
          setText(toggle.querySelector('.btn-label') || toggle, full.hidden ? t('studio.showMore', { n: text.length }) : t('studio.showLess'));
        });
        value = h('div', {}, short, full, toggle);
      } else {
        value = h('p', { class: def.type === 'longtext' ? 'st-longtext' : 'st-value' }, text);
      }
    }
    rows.push(h('div', { class: 'st-field', dataset: { key: k } }, h('dt', { class: 'st-field-label mono-label' }, def.label), h('dd', { class: 'st-field-value' }, value)));
  }
  return rows;
}

/**
 * The fields as a form (« Modifier », operate): title, texts, numbers, links; JSON stays read-only.
 * « Enregistrer » sends PATCH /api/studio/items/:id {title?, data} (emptied fields → null) and
 * gives the updated element to `onSaved`; nothing changed → `onCancel`.
 * @param {object} ctx app context
 * @param {{id: string, kind: string, title: string, data: object}} item
 * @param {{fields?: object[]}|null} type
 * @param {{onSaved: (item: object) => void, onCancel: () => void}} hooks
 * @returns {HTMLElement}
 */
export function editForm(ctx, item, type, { onSaved, onCancel }) {
  const person = item.kind === 'person';
  const title = input({ value: item.title, maxLength: 200, class: 'st-edit-title' });
  const titleField = field(person ? t('studio.edit.titlePerson') : t('studio.edit.title'), title);
  const controls = [];
  const rows = [titleField];
  for (const def of type?.fields || []) {
    const v = item.data?.[def.key];
    if (def.type === 'json') {
      if (v === undefined) continue;
      rows.push(field(def.label, h('pre', { class: 'st-json' }, JSON.stringify(v, null, 2)), { hint: t('studio.edit.readonly') }));
      continue;
    }
    const value = v === undefined || v === null ? '' : String(v);
    const control = def.type === 'longtext'
      ? textarea({ value, rows: Math.min(12, Math.max(3, value.split('\n').length + 1)), class: 'st-edit-field', dataset: { key: def.key } })
      : input({ value, type: def.type === 'number' ? 'text' : def.type === 'url' ? 'url' : 'text', inputMode: def.type === 'number' ? 'decimal' : null, class: 'st-edit-field', dataset: { key: def.key } });
    if (def.type === 'longtext') control.value = value;
    controls.push({ def, control, before: value });
    rows.push(field(def.label, control, { optional: true }));
  }
  const save = button(t('studio.edit.save'), { variant: 'primary', size: 'sm', class: 'st-edit-save' });
  const cancel = button(t('studio.edit.cancel'), { variant: 'ghost', size: 'sm', class: 'st-edit-cancel', onClick: () => onCancel() });
  save.addEventListener('click', async () => {
    const patch = {};
    const nextTitle = title.value.trim();
    if (!nextTitle) { titleField.setError(t('studio.new.required')); return; }
    if (nextTitle !== item.title) patch.title = nextTitle;
    const data = {};
    for (const { def, control, before } of controls) {
      const now = def.type === 'longtext' ? control.value : control.value.trim();
      if (now === before) continue;
      if (now.trim() === '') { data[def.key] = null; continue; }
      if (def.type === 'number') {
        const n = Number(now.replace(',', '.'));
        if (!Number.isFinite(n)) { ctx.toast.error(t('studio.edit.number', { label: def.label })); return; }
        data[def.key] = n;
      } else {
        data[def.key] = now;
      }
    }
    if (Object.keys(data).length) patch.data = data;
    if (!Object.keys(patch).length) { onCancel(); return; }
    save.disabled = true;
    try {
      const updated = await ctx.api.patch(`/api/studio/items/${enc(item.id)}`, patch);
      ctx.toast.success(t('studio.edit.saved'), { timeout: 2500 });
      onSaved(updated);
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      save.disabled = false;
    }
  });
  return h('div', { class: 'st-edit-form' }, ...rows, h('div', { class: 'st-edit-actions' }, cancel, save));
}

/**
 * Send files one by one to an element (POST …/upload?name=&role=, the file as the body, 60 Mo at
 * most each): a toast per refused file, one for the files added. `btn` shows « Envoi 2/3… », then
 * `idleLabel` again (« Ajouter des fichiers » by default).
 * @param {object} ctx
 * @param {string} id the element
 * @param {File[]} files
 * @param {string} role '' for none
 * @param {{btn?: HTMLElement|null, idleLabel?: string, isDestroyed?: () => boolean}} [opts]
 * @returns {Promise<number>} the files added
 */
export async function uploadFiles(ctx, id, files, role, { btn = null, idleLabel = null, isDestroyed = () => false } = {}) {
  const label = btn ? btn.querySelector('.btn-label') || btn : null;
  if (btn) btn.disabled = true;
  let done = 0;
  uploading += 1;
  try {
    for (const [i, file] of files.entries()) {
      if (isDestroyed()) return done;
      if (label) setText(label, t('studio.files.sending', { i: i + 1, n: files.length }));
      if (file.size > UPLOAD_MAX_BYTES) {
        ctx.toast.error(t('studio.files.failed', { name: file.name, error: t('studio.files.tooBig') }));
        continue;
      }
      try {
        await ctx.api.upload(ctx.api.withQuery(`/api/studio/items/${enc(id)}/upload`, { name: file.name, ...(role ? { role } : {}) }), file);
        done++;
      } catch (err) {
        ctx.toast.error(t('studio.files.failed', { name: file.name, error: err?.message || '' }));
      }
    }
  } finally {
    uploading -= 1;
    if (btn) btn.disabled = false;
    if (label) setText(label, idleLabel ?? t('studio.files.add'));
  }
  if (done) ctx.toast.success(t('studio.files.done', { n: done }), { timeout: 2500 });
  return done;
}

/** Uploads running (uploadFiles, holdUploads) in this page. */
let uploading = 0;

/**
 * Counts files being sent outside uploadFiles (« Ajouter des hooks », web/ui/studio/hooks.js), so
 * live reloads and the app-update reload wait meanwhile; the returned function ends it (once).
 * @returns {() => void}
 */
export function holdUploads() {
  uploading += 1;
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    uploading -= 1;
  };
}

/**
 * A studio page must not be rebuilt by a live update now (pure but for the DOM read): files are
 * being sent (the page shows their progress), or a video or a sound of `root` is playing.
 * @param {HTMLElement} root
 * @returns {boolean}
 */
export function liveReloadWaits(root) {
  if (uploading > 0) return true;
  return [...(root?.querySelectorAll?.('video,audio') || [])].some((m) => m.paused === false && !m.ended);
}

/**
 * The files of an element: « Ajouter des fichiers » with its role (operate), then one tile per
 * file (image, video, audio or document) with its role, its removal (operate, after a
 * confirmation) and its download; « Aucun fichier joint. » without any.
 * @param {object} ctx
 * @param {{id: string, files: object[]}} item
 * @param {{operate: boolean, roles: readonly string[], onChanged: () => void, isDestroyed?: () => boolean}} opts
 * @returns {HTMLElement}
 */
export function filesPanel(ctx, item, { operate, roles, onChanged, isDestroyed = () => false }) {
  let tools = null;
  if (operate) {
    const role = select(roles.map((r) => ({ value: r, label: roleLabel(r) })), { class: 'st-upload-role', 'aria-label': t('studio.files.role'), value: roles[0] });
    const picker = h('input', { type: 'file', multiple: true, accept: UPLOAD_ACCEPT, class: 'st-upload-input', hidden: true });
    const add = button(t('studio.files.add'), { variant: 'secondary', size: 'sm', icon: 'upload', class: 'st-upload', onClick: () => picker.click() });
    picker.addEventListener('change', async () => {
      const files = [...(picker.files || [])];
      picker.value = '';
      if (!files.length) return;
      await uploadFiles(ctx, item.id, files, role.value, { btn: add, isDestroyed });
      if (!isDestroyed()) onChanged();
    });
    tools = h('div', { class: 'st-upload-bar' }, h('label', { class: 'st-select-wrap' }, icon('layers', { size: 15 }), role), add, picker);
  }
  const removeFile = async (f) => {
    const ok = await ctx.overlay.confirm({
      title: t('studio.files.remove.title', { name: f.name }), message: t('studio.files.remove.message'),
      confirmLabel: t('studio.files.remove.confirm'), danger: true,
    });
    if (!ok || isDestroyed()) return;
    try {
      await ctx.api.del(`/api/studio/items/${enc(item.id)}/files/${enc(f.name)}`);
      ctx.toast.success(t('studio.files.removed'), { timeout: 2500 });
      onChanged();
    } catch (err) {
      ctx.toast.error(err);
    }
  };
  if (!item.files.length) return h('div', {}, tools, h('p', { class: 'st-muted' }, t('studio.noFiles')));
  const tiles = item.files.map((f) => {
    let media;
    if (f.media === 'image') media = h('a', { href: f.url, target: '_blank', rel: 'noopener', class: 'st-file-media' }, h('img', { src: f.url, alt: f.name, loading: 'lazy' }));
    else if (f.media === 'video') media = h('div', { class: 'st-file-media' }, h('video', { src: f.url, controls: true, preload: 'metadata', playsinline: true }));
    else if (/^audio\//.test(f.mime || '')) media = h('div', { class: 'st-file-media is-audio' }, h('audio', { src: f.url, controls: true, preload: 'none' }));
    else media = h('div', { class: 'st-file-media is-doc' }, icon('inbox', { size: 22 }));
    return h('figure', { class: 'st-file', dataset: { name: f.name } }, media,
      f.role ? h('span', { class: 'st-file-role', dataset: { role: f.role } }, roleLabel(f.role)) : null,
      operate ? iconButton('close', t('studio.files.remove', { name: f.name }), { class: 'st-file-remove', size: 14, onClick: () => removeFile(f) }) : null,
      h('figcaption', { class: 'st-file-cap' },
        h('span', { class: 'st-file-name', title: f.name }, f.name),
        h('span', { class: 'st-file-meta mono-label' }, f.size ? formatBytes(f.size) : ''),
        h('a', { class: 'st-file-dl', href: f.url, download: f.name, 'aria-label': `${t('studio.download')} ${f.name}` }, t('studio.download'))));
  });
  return h('div', {}, tools, h('div', { class: 'st-files' }, ...tiles));
}

/**
 * The history of an element, newest first: what (status change with the type's labels, fields
 * changed…), who (agent or person), its note, the task of a « Regénérer », when (`dated`: the
 * date too).
 * @param {{history?: object[]}} item
 * @param {object|null} type
 * @param {(id: string) => object|null} agentById
 * @param {{dated?: boolean}} [opts]
 * @returns {HTMLOListElement}
 */
export function historyList(item, type, agentById, { dated = false } = {}) {
  const label = (key) => statusInfo(type, key).label;
  const entries = [...(item.history || [])].reverse().map((e) => {
    const who = e.by?.agentId ? (agentById(e.by.agentId)?.name || e.by.agentId) : (e.by?.name || '');
    let what = t(`studio.h.${e.action}`);
    if (e.action === 'status') what = t('studio.h.status', { from: label(e.from), to: label(e.to) });
    else if (e.action === 'update') what = t('studio.h.update', { fields: (e.fields || []).join(', ') });
    else if (e.action === 'title') what = t('studio.h.title', { from: e.from || '', to: e.to || '' });
    else if (what === `studio.h.${e.action}`) what = e.action;
    return h('li', { class: 'st-hist' },
      h('span', { class: 'st-hist-dot', 'aria-hidden': 'true' }),
      h('div', { class: 'st-hist-body' },
        h('p', { class: 'st-hist-what' }, what, who ? h('span', { class: 'st-hist-who' }, ` ${t('studio.h.by', { who })}`) : null),
        e.note ? h('p', { class: 'st-hist-note' }, e.note) : null,
        e.taskId ? h('a', { class: 'st-hist-task', href: `#/task/${enc(e.taskId)}` }, t('studio.regen.open')) : null,
        h('time', { class: 'st-hist-at mono-label', title: formatDateTime(e.at) }, dated ? `${relativeTime(e.at)} · ${formatDateTime(e.at)}` : relativeTime(e.at))));
  });
  return h('ol', { class: 'st-history' }, ...entries);
}
